import { useId, type ReactElement } from "react";
import type { LamaPriorityRoadmapOutput } from "@playbookai/widget-schemas";
import { Badge } from "../../atoms/Badge/Badge.js";
import { Button } from "../../atoms/Button/Button.js";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
  lamaConfidenceLabel,
  lamaScorePercentLabel,
} from "../lama-report-presentation.js";

/**
 * LAMA Priority Roadmap — the curated lowest-scoring areas re-scored from the
 * latest wave, ported data-blind from `LamaReportPage`. An entry the latest
 * wave did not measure keeps its curated score and is marked `stale`; the
 * badge says so rather than presenting an old number as current (the old page
 * derived the flag but never rendered it — plan §13.3 requires it).
 *
 * Suppressed is its own state: withheld to protect anonymity, never shown as
 * "no priorities".
 */
export function LamaPriorityRoadmapWidget({
  data,
  itemAction,
}: WidgetBodyProps<LamaPriorityRoadmapOutput>): ReactElement {
  /* Per-instance id root for the disabled-reason text below. `useId` rather
     than the question id alone: two roadmap instances would otherwise mint the
     same element ids, and `aria-describedby` would resolve to the first. */
  const reasonIdBase = useId();
  // `disabledReason` is the documented name; `reason` is its deprecated
  // alias. The type forbids setting both, so reading them in this order
  // always resolves to whichever one the caller set.
  const itemActionReason = itemAction?.disabledReason;
  if (data.suppressed) {
    return (
      <div>
        <p className="text-sm font-medium text-pb-text">
          {LAMA_SUPPRESSED_TITLE}
        </p>
        <p className="mt-1 text-sm text-pb-text-muted">
          {LAMA_SUPPRESSED_BODY}
        </p>
      </div>
    );
  }
  if (data.entries.length === 0) {
    return (
      <p className="text-sm text-pb-text-muted">
        No priorities available — recommendations will appear when the report
        contains roadmap items.
      </p>
    );
  }
  return (
    <div>
      <p className="mb-2 text-xs text-pb-text-muted">
        Confidence: {lamaConfidenceLabel(data.confidence)} · lowest-scoring
        areas in the latest wave
      </p>
      <div className="divide-y divide-pb-border rounded-lg border border-pb-border">
        {data.entries.map((entry, index) => (
          <article
            /* The QUESTION id, never `area`. `area` is the prompt TEXT and
               duplicate prompts within a version are legal, so keying on it
               produces duplicate sibling keys and lets reconciliation hand the
               busy/disabled state to the wrong row. Every other identity in this
               row — `busyItemId`, the reason id, `onSelect` — already keys on
               `questionId`; this was the one that did not. */
            key={entry.questionId}
            className="flex flex-wrap items-center gap-3 p-4"
          >
            <span className="w-5 text-center text-xs font-semibold text-pb-text-muted pb-tabular">
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-pb-text">
                {entry.area}
              </h3>
              <p className="mt-1 text-xs text-pb-text-muted">{entry.pillar}</p>
            </div>
            {entry.stale ? (
              <Badge variant="outline">Curated score</Badge>
            ) : null}
            <span className="text-sm font-semibold text-pb-red-text pb-tabular">
              {lamaScorePercentLabel(entry.score)}
            </span>
            {/*
              The action arrives as a PROP. It used to be a hardcoded disabled
              button reading "Kaizen integration later" — which was doubly wrong:
              the routes had been live since PB-191 Tasks 27-29, and what they
              mint is a SOLVE, not a Kaizen artifact. This widget still knows
              none of that; it renders a label and calls back with the item id.

              Absent action renders NOTHING rather than a disabled placeholder:
              a control no state can enable is not information about this row.
            */}
            {itemAction ? (
              <div className="flex w-full flex-col items-stretch gap-1 @sm/content:w-auto @sm/content:items-end">
                <Button
                  variant="outline"
                  className="w-full justify-center @sm/content:w-auto"
                  disabled={
                    itemActionReason !== undefined ||
                    itemAction.busyItemId === entry.questionId
                  }
                  {...(itemActionReason !== undefined
                    ? {
                        "aria-describedby": `${reasonIdBase}-${entry.questionId}`,
                      }
                    : {})}
                  onClick={() => itemAction.onSelect(entry.questionId)}
                >
                  {itemAction.busyItemId === entry.questionId
                    ? "Working…"
                    : itemAction.label}
                </Button>
                {/*
                  VISIBLE text, wired with `aria-describedby` — never a `title`.
                  A disabled native button takes no focus and fires no reliable
                  hover, so a tooltip reason is unreachable by keyboard and
                  unreliable by mouse. It is also the rule this package already
                  states: "disabled with the real reason as visible text, never
                  hidden and never a tooltip". `ActionMenu` enforces the same
                  thing structurally, and the report host's Export command is
                  wired exactly this way.
                */}
                {itemActionReason !== undefined ? (
                  <p
                    id={`${reasonIdBase}-${entry.questionId}`}
                    className="text-xs text-pb-text-muted"
                  >
                    {itemActionReason}
                  </p>
                ) : null}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}
